import dayjs from 'dayjs';
import Image from 'next/image';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AiOutlineClockCircle, AiOutlineEnvironment, AiOutlineInfoCircle, AiOutlineTeam } from 'react-icons/ai';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { FaBolt, FaCoffee, FaLeaf, FaParking, FaPrint, FaSnowflake, FaTv, FaWheelchair, FaWifi } from 'react-icons/fa';
import { IoArrowBack, IoChevronBack, IoChevronForward } from 'react-icons/io5';

import { Location, Unit } from '@core/booking/booking.types';
import { useSettingsStore } from '@core/settings/store/settings';

import config from '@config';

interface Amenity {
  name: string;
  icon?: string;
}

interface MeetingRoomDetailProps {
  room: Unit;
  location: Location;
  onBack: () => void;
  onBook: () => void;
}

function normalizeAmenity(item: unknown): Amenity | null {
  if (typeof item === 'string') return { name: item };
  if (typeof item === 'object' && item !== null && 'name' in item) {
    const obj = item as Record<string, unknown>;
    return { name: String(obj.name), icon: obj.icon ? String(obj.icon) : undefined };
  }
  return null;
}

function parseAmenities(raw?: string | Record<string, unknown>): Amenity[] {
  if (!raw) return [];
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (Array.isArray(data)) {
    return data.map(normalizeAmenity).filter(Boolean) as Amenity[];
  }
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    const predefined = Array.isArray(obj.predefined)
      ? (obj.predefined.map(normalizeAmenity).filter(Boolean) as Amenity[])
      : [];
    const custom = Array.isArray(obj.custom) ? (obj.custom.map(normalizeAmenity).filter(Boolean) as Amenity[]) : [];
    return [...predefined, ...custom];
  }
  return [];
}

function cloudinaryUrl(url: string, width: number): string {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  const transforms = `q_auto:best,f_auto,w_${width},c_fill,dpr_2.0`;
  return url.replace('/upload/', `/upload/${transforms}/`);
}

function parseExtraImages(extraImagesStr?: string): string[] {
  if (!extraImagesStr) return [];
  let data: unknown = extraImagesStr;
  if (typeof extraImagesStr === 'string') {
    try {
      data = JSON.parse(extraImagesStr);
    } catch {
      return [];
    }
  }
  return Array.isArray(data) ? data.filter((url) => typeof url === 'string' && url.length > 0) : [];
}

function formatTime(time?: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  return dayjs().hour(parseInt(hours, 10)).minute(parseInt(minutes, 10)).format('h:mm A');
}

const FA_ICON_MAP: Record<string, FC<{ size: number }>> = {
  'fa-leaf': FaLeaf,
  'fa-wifi': FaWifi,
  'fa-coffee': FaCoffee,
  'fa-tv': FaTv,
  'fa-snowflake': FaSnowflake,
  'fa-print': FaPrint,
  'fa-parking': FaParking,
  'fa-bolt': FaBolt,
  'fa-wheelchair': FaWheelchair,
};

interface BulkDiscount {
  hours: number;
  percentage: number;
}

function parseBulkDiscounts(str?: string): BulkDiscount[] {
  if (!str) return [];
  try {
    const data = JSON.parse(str);
    if (!Array.isArray(data)) return [];
    return data.filter(
      (d: unknown) =>
        typeof d === 'object' &&
        d !== null &&
        typeof (d as any).hours === 'number' &&
        typeof (d as any).percentage === 'number',
    ) as BulkDiscount[];
  } catch {
    return [];
  }
}

const AmenityIcon: FC<{ icon?: string }> = ({ icon }) => {
  if (icon && FA_ICON_MAP[icon]) {
    const IconComp = FA_ICON_MAP[icon];
    return <IconComp size={16} />;
  }
  return <AiOutlineInfoCircle size={16} />;
};

const MeetingRoomDetail: FC<MeetingRoomDetailProps> = ({ room, location, onBack, onBook }) => {
  const { t } = useTranslation();
  const { textColor } = useSettingsStore();
  const images = useMemo(() => {
    const allImages: string[] = [];
    if (room.image_link) {
      allImages.push(room.image_link);
    }
    const extras = parseExtraImages(room.extra_images);
    allImages.push(...extras);
    return allImages.length > 0 ? allImages : ['/placeholder.webp'];
  }, [room.image_link, room.extra_images]);

  const allAmenities = useMemo(() => parseAmenities(room.amenities), [room.amenities]);
  const bulkDiscounts = useMemo(() => parseBulkDiscounts(room.bulk_discounts), [room.bulk_discounts]);
  const [discountsExpanded, setDiscountsExpanded] = useState(false);

  const hasDescription = !!room.external_description;
  const hasHours = !!room.external_hours_from && !!room.external_hours_to;
  const hasAccess = !!room.external_how_to_access;
  const hasAmenities = allAmenities.length > 0;

  // Carousel state for when there are more than 3 images
  const [carouselStart, setCarouselStart] = useState(0);
  const visibleCount = images.length === 1 ? 1 : images.length === 2 ? 2 : 3;
  const canGoBack = carouselStart > 0;
  const canGoForward = carouselStart + visibleCount < images.length;

  const handlePrev = useCallback(() => {
    setCarouselStart((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCarouselStart((prev) => Math.min(images.length - visibleCount, prev + 1));
  }, [images.length, visibleCount]);

  const visibleImages = images.slice(carouselStart, carouselStart + visibleCount);

  return (
    <div className="w-full overflow-y-auto no-scrollbar">
      {/* Back button */}
      <div className="px-4 md:px-16 pt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-0"
          style={{ color: 'var(--main)' }}
        >
          <IoArrowBack size={20} className="rtl:rotate-180" />
          <span className="text-base font-medium">{t('landing.detail.backToRooms')}</span>
        </button>
      </div>

      {/* Image gallery */}
      <div className="px-4 md:px-16 pt-4 pb-6">
        <div className="relative">
          {/* Navigation arrows */}
          {canGoBack && (
            <button
              onClick={handlePrev}
              className="absolute start-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center border-none cursor-pointer hover:shadow-lg transition-shadow"
            >
              <IoChevronBack size={20} className="rtl:rotate-180" style={{ color: '#404D61' }} />
            </button>
          )}
          {canGoForward && (
            <button
              onClick={handleNext}
              className="absolute end-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center border-none cursor-pointer hover:shadow-lg transition-shadow"
            >
              <IoChevronForward size={20} className="rtl:rotate-180" style={{ color: '#404D61' }} />
            </button>
          )}

          {/* Image grid */}
          {images.length === 1 ? (
            <div className="rounded-xl overflow-hidden" style={{ height: 420 }}>
              <Image
                src={cloudinaryUrl(visibleImages[0], 1200)}
                alt={room.name}
                width={1200}
                height={420}
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                unoptimized
              />
            </div>
          ) : images.length === 2 ? (
            <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden" style={{ height: 420 }}>
              {visibleImages.map((src, i) => (
                <div key={`${carouselStart}-${i}`} className="relative overflow-hidden">
                  <Image
                    src={cloudinaryUrl(src, 800)}
                    alt={`${room.name} ${carouselStart + i + 1}`}
                    width={800}
                    height={420}
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    unoptimized
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex gap-2 rounded-xl overflow-hidden" style={{ height: 420 }}>
              {/* Large left image */}
              <div key={`${carouselStart}-main`} className="relative overflow-hidden flex-1" style={{ minWidth: 0 }}>
                <Image
                  src={cloudinaryUrl(visibleImages[0], 800)}
                  alt={`${room.name} ${carouselStart + 1}`}
                  width={800}
                  height={420}
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  unoptimized
                />
              </div>
              {/* Right column - 2 stacked images */}
              <div className="flex flex-col gap-2 flex-1" style={{ minWidth: 0 }}>
                {visibleImages.slice(1, 3).map((src, i) => (
                  <div
                    key={`${carouselStart}-${i + 1}`}
                    className="relative overflow-hidden flex-1"
                    style={{ minHeight: 0 }}
                  >
                    <Image
                      src={cloudinaryUrl(src, 600)}
                      alt={`${room.name} ${carouselStart + i + 2}`}
                      width={600}
                      height={420}
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      unoptimized
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Image counter */}
          {images.length > 1 && (
            <div
              className="absolute bottom-3 end-3 px-3 py-1 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            >
              {t('landing.detail.imageCounter', {
                from: carouselStart + 1,
                to: Math.min(carouselStart + visibleCount, images.length),
                total: images.length,
              })}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-16 pb-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left column - details */}
          <div className="flex-1">
            {/* Room name and location */}
            <h1 className="text-3xl md:text-4xl font-bold m-0 mb-2" style={{ color: textColor }}>
              {room.name}
            </h1>
            <p className="text-base m-0 mb-1" style={{ color: textColor, opacity: 0.6 }}>
              {location.name}
            </p>
            <p className="text-sm m-0 mb-6" style={{ color: textColor, opacity: 0.5 }}>
              {location.address}
            </p>

            {/* Capacity */}
            <div className="flex items-center gap-2 mb-6 px-4 py-3 rounded-lg" style={{ backgroundColor: '#F8F9FA' }}>
              <AiOutlineTeam size={20} style={{ color: 'var(--main)' }} />
              <span className="text-base font-medium" style={{ color: '#404D61' }}>
                {t('landing.upToPeople', { count: Number(room.max_people_number) })}
              </span>
            </div>

            {/* Description */}
            {hasDescription && (
              <div className="mb-6">
                <h2 className="text-xl font-bold m-0 mb-3" style={{ color: textColor }}>
                  {t('landing.detail.aboutThisSpace')}
                </h2>
                <p className="text-base m-0 leading-relaxed" style={{ color: textColor, opacity: 0.6 }}>
                  {room.external_description}
                </p>
              </div>
            )}

            {/* Hours */}
            {hasHours && (
              <div className="mb-6">
                <h2 className="text-xl font-bold m-0 mb-3" style={{ color: textColor }}>
                  {t('landing.detail.availableHours')}
                </h2>
                <div className="flex items-center gap-2" style={{ color: textColor, opacity: 0.6 }}>
                  <AiOutlineClockCircle size={18} style={{ color: 'var(--main)' }} />
                  <span className="text-base">
                    {formatTime(room.external_hours_from)} – {formatTime(room.external_hours_to)}
                  </span>
                </div>
              </div>
            )}

            {/* How to access */}
            {hasAccess && (
              <div className="mb-6">
                <h2 className="text-xl font-bold m-0 mb-3" style={{ color: textColor }}>
                  {t('landing.detail.howToAccess')}
                </h2>
                <div className="flex items-start gap-2" style={{ color: textColor, opacity: 0.6 }}>
                  <AiOutlineEnvironment size={18} style={{ color: 'var(--main)', marginTop: 3 }} />
                  <span className="text-base leading-relaxed">{room.external_how_to_access}</span>
                </div>
              </div>
            )}

            {/* Amenities */}
            {hasAmenities && (
              <div className="mb-6">
                <h2 className="text-xl font-bold m-0 mb-3" style={{ color: textColor }}>
                  {t('landing.detail.amenities')}
                </h2>
                <div className="flex flex-wrap gap-3">
                  {allAmenities.map((amenity) => (
                    <div
                      key={amenity.name}
                      className="flex items-center gap-2 px-4 py-2 rounded-full"
                      style={{ backgroundColor: '#F8F9FA', color: '#404D61' }}
                    >
                      <AmenityIcon icon={amenity.icon} />
                      <span className="text-sm font-medium">{amenity.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column - book button card */}
          <div className="lg:w-[340px] flex-shrink-0">
            <div
              className="sticky top-6 rounded-xl p-6 shadow-md"
              style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E8E8' }}
            >
              <h3 className="text-lg font-bold m-0 mb-2" style={{ color: '#404D61' }}>
                {room.name}
              </h3>
              <p className="text-sm m-0 mb-1" style={{ color: '#707070' }}>
                {location.name}
              </p>
              <div className="flex items-center gap-1 mb-4" style={{ color: '#999' }}>
                <AiOutlineTeam size={14} />
                <span className="text-xs">{t('landing.upToPeople', { count: Number(room.max_people_number) })}</span>
              </div>

              {/* Bulk Discounts */}
              {bulkDiscounts.length > 0 && (
                <div
                  className="rounded-lg p-3 mb-4"
                  style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}
                >
                  <p className="text-sm font-bold m-0 mb-2" style={{ color: '#166534' }}>
                    {t('landing.detail.bulkDiscountsTitle')}
                  </p>
                  {(bulkDiscounts.length <= 4
                    ? bulkDiscounts
                    : discountsExpanded
                    ? bulkDiscounts
                    : bulkDiscounts.slice(0, 4)
                  ).map((d) => (
                    <div key={d.hours} className="flex items-center gap-2 py-1">
                      <span
                        className="flex items-center justify-center rounded-full text-xs font-bold"
                        style={{
                          width: 22,
                          height: 22,
                          backgroundColor: '#DCFCE7',
                          color: '#166534',
                          flexShrink: 0,
                        }}
                      >
                        %
                      </span>
                      <span className="text-sm" style={{ color: '#404D61' }}>
                        <Trans
                          i18nKey="landing.detail.bulkDiscountLine"
                          count={d.hours}
                          values={{ percentage: d.percentage }}
                          components={{ strong: <strong style={{ color: '#166534' }} /> }}
                        />
                      </span>
                    </div>
                  ))}
                  {bulkDiscounts.length > 4 && (
                    <button
                      type="button"
                      onClick={() => setDiscountsExpanded((prev) => !prev)}
                      className="flex items-center gap-1 mt-1 text-xs font-medium border-none bg-transparent cursor-pointer p-0"
                      style={{ color: '#166534' }}
                    >
                      <span>
                        {discountsExpanded
                          ? t('landing.detail.showLess')
                          : t('landing.detail.showAllDiscounts', { count: bulkDiscounts.length })}
                      </span>
                      {discountsExpanded ? <IoChevronUp size={12} /> : <IoChevronDown size={12} />}
                    </button>
                  )}
                </div>
              )}

              <button
                onClick={onBook}
                className="w-full py-3 rounded-lg text-white font-bold text-base border-none cursor-pointer transition-opacity hover:opacity-90"
                style={{ backgroundColor: 'var(--main)' }}
              >
                {t('landing.bookNow')}
              </button>
            </div>
          </div>
        </div>

        {/* Map section */}
        {location.address && (
          <div className="mt-8">
            <h2 className="text-xl font-bold m-0 mb-4" style={{ color: textColor }}>
              {t('landing.detail.location')}
            </h2>
            <div className="rounded-xl overflow-hidden">
              <iframe
                title={t('landing.detail.mapOf', { name: location.name })}
                width="100%"
                height="350"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${
                  config.backend.google.map.apiKey
                }&q=${encodeURIComponent(location.address)}`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingRoomDetail;
